import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, useFormikContext } from 'formik';
import { get } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';

import ThreeDotsMenu from '../../../../../core/ThreeDotsMenu';
import ReorderDragHandle from '../../components/ReorderDragHandle';
import { isNewRow, markNewRow } from '../../newRowIdentity';
import { reorderArrayField } from '../../reorderArrayField';
import SortAzButton from '../../SortAzButton/SortAzButton';
import BaseConfigurationSection from '../BaseConfigurationSection';

import IncidentTypeConfigurationCard from './IncidentTypeConfigurationCard';

const ID_PREFIX = 'configuration.incident_details.types';

const getNewField = ({ index }) =>
  markNewRow({
    id: `incident_type_${index}`,
    desc: `Incident Type ${index}`,
    active: true,
    sub_types: [
      {
        id: '',
        desc: '',
        category: undefined,
        active: true,
        [CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY]: '',
      },
    ],
  });

const IncidentTypesSubTypesConfigurationArray = ({ disabled = false }) => {
  const { values, initialValues } = useFormikContext();

  const initialIncidentTypes = get(initialValues, ID_PREFIX);
  const incidentTypes = get(values, ID_PREFIX);

  // A type is "new" (not yet saved) when it was added in this editing session, or - for a type that
  // came from the server, which carries no marker - when its stable id isn't in the saved set. The
  // marker is what keeps a type added here new after the Display Name syncs its Key into a saved
  // type's key, which would otherwise read as saved and lock the row out for good. The id fallback
  // is what keeps the verdict right after a DnD reorder: rows are keyed by position, so deciding by
  // array position would read a saved slot belonging to a different type and flip the verdict.
  // Delete is allowed for exactly the same reason (only unsaved types can be removed), so both
  // derive from identity.
  const isNewType = (index) =>
    isNewRow(incidentTypes?.[index]) || !initialIncidentTypes?.find((f) => f.id === incidentTypes?.[index]?.id);
  const isDeleteAllowed = isNewType;

  return (
    <BaseConfigurationSection
      title="Edit Incident Type and Subtype"
      noGridContainer
      actions={<SortAzButton fieldId={ID_PREFIX} sortKey="desc" disabled={disabled} />}
    >
      <FieldArray
        name={ID_PREFIX}
        render={(arrayHelpers) => (
          <div className="mr-6 flex flex-col gap-12">
            <DndOrderedList
              className="flex flex-col gap-12"
              // The draggable row is a flex item, so the card's own bottom margin (CollapsibleWrapper
              // sets margin-bottom) is trapped inside the row and renders as a white strip under the
              // card in the drag preview. Row spacing comes from the list gap, so zero that margin.
              itemClassName="[&>*>*]:!mb-0"
              disabled={disabled}
              items={(Array.isArray(incidentTypes) ? incidentTypes : []).map((type, idx) => ({
                type,
                idx,
                // Key on the position, not the type's `id`. Incident-type ids are user-editable
                // (Display Name syncs to Key via snakeCase), so an id-derived key changes mid-edit
                // and remounts the row, dropping input focus. The index is stable while typing.
                dndId: String(idx),
              }))}
              getItemId={(item) => item.dndId}
              onReorder={({ fromIndex, toIndex }) => reorderArrayField(arrayHelpers, fromIndex, toIndex)}
              renderItem={({ idx }, { dragHandleProps }) => (
                <IncidentTypeConfigurationCard
                  fieldId={`${ID_PREFIX}[${idx}]`}
                  isNewField={isNewType(idx)}
                  actions={
                    <div className="flex items-center justify-center">
                      <ReorderDragHandle dragHandleProps={dragHandleProps} disabled={disabled} />
                      <ThreeDotsMenu
                        stopPropagation
                        disabled={!isDeleteAllowed(idx)}
                        options={[
                          {
                            onClick: () => arrayHelpers.remove(idx),
                            node: 'Remove',
                            key: 'remove',
                            disabled: !isDeleteAllowed(idx),
                          },
                        ]}
                      />
                    </div>
                  }
                />
              )}
            />
            <ErrorHelperTextFormik id={ID_PREFIX} withoutChildren />
            <div className="flex">
              <Button
                variant="text"
                color="primary"
                onClick={() => arrayHelpers.push(getNewField({ index: incidentTypes?.length + 1 }))}
                disabled={disabled}
              >
                <AddIcon />
                ADD INCIDENT TYPE
              </Button>
            </div>
          </div>
        )}
      />
    </BaseConfigurationSection>
  );
};

IncidentTypesSubTypesConfigurationArray.propTypes = {
  fieldId: PropTypes.string.isRequired,
  section: PropTypes.string,
  disabled: PropTypes.bool,
};

export default IncidentTypesSubTypesConfigurationArray;
